import { MedplumClient } from '@medplum/core'
import type { Patient } from '@medplum/fhirtypes'

export type UserStatus = 'invited' | 'active' | 'inactive'

export function capitalizeFirstLetter(str: string): string {
  if (!str) return ''
  return str.charAt(0).toUpperCase() + str.slice(1)
}

function formatPrettyDate(date: Date): string {
  return date.toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  })
}
export const refreshPatientStatus = async (patientId: string, medplum: MedplumClient) => {
  const freshPatient = await medplum.readResource('Patient', patientId || '')
  if (freshPatient) {
    return freshPatient
  }
}
export const STATUS_CONFIG: Record<
  UserStatus,
  { label: string; color: 'yellow' | 'green' | 'gray' }
> = {
  invited: { label: 'invited', color: 'yellow' },
  active: { label: 'active', color: 'green' },
  inactive: { label: 'inactive', color: 'gray' },
}

// Mirror of middleware's extension URLs (patient/handlers/patient-status.ts). Note the two differ in
// host/scheme, matching what is written on the resource.
const LAST_INVITATION_SENT_URL =
  'http://www.behuman.health/fhir/StructureDefinition/last-invitation-sent'
const LAST_LOGIN_URL = 'https://behuman.health/fhir/StructureDefinition/lastLogin'

function extensionDateTime(patient: Patient, url: string): string | null {
  return patient.extension?.find((ext) => ext.url === url)?.valueDateTime ?? null
}

type PortalActivity = { lastLogin?: string | null; lastInvitationSent?: string | null }

/**
 * Build the status tooltip from raw activity timestamps (ISO date-time). Used by callers that already
 * have the values as plain fields (e.g. the patient list, sourced from /v2/patients) instead of a FHIR
 * resource.
 */
export function getActivityTooltip(
  status: string,
  { lastLogin, lastInvitationSent }: PortalActivity,
): string {
  const lastInviteSentTime = lastInvitationSent
    ? formatPrettyDate(new Date(lastInvitationSent))
    : null
  const lastLoginTime = lastLogin ? formatPrettyDate(new Date(lastLogin)) : null
  switch (status) {
    case 'invited':
      return lastInviteSentTime
        ? `Patient Portal invitation sent on ${lastInviteSentTime}.`
        : 'No information available.'
    case 'active':
      return lastLoginTime
        ? `Patient last logged into the portal on ${lastLoginTime}.`
        : 'No information available.'
    case 'inactive':
      return lastLoginTime
        ? `Patient has not logged into the portal for more than 90 days. Last login was on ${lastLoginTime}.`
        : 'No information available.'
    default:
      return 'No information available.'
  }
}

export function getTooltipText(status: string, patient: Patient): string {
  return getActivityTooltip(status, {
    lastLogin: extensionDateTime(patient, LAST_LOGIN_URL),
    lastInvitationSent: extensionDateTime(patient, LAST_INVITATION_SENT_URL),
  })
}
